import type { PeopleFilter, FolderId } from '../../../../service';
import {
    ContactFolder,
    ContactFoldersData,
    FolderGroupType,
    isConsumer,
    getLogger,
    WellKnownNetworkNames,
    rootContactFolderDistinguishedName,
} from '../../../../shared';
import type BaseFolderType from 'owa-service/lib/contract/BaseFolderType';

export function convertFiltersToFolderData(peopleFilters: PeopleFilter[]): ContactFoldersData {
    logFolderStatistics(peopleFilters);

    // Filter out all "hidden folders", as defined in
    // //depot/Chicago/sources/Dev/Owa/src/Client/Core/PeopleHub/Models/PeopleFilterGroupPriorities.cs
    const filtersToDisplay = peopleFilters.filter(isSupportedFolder);

    // The root contact folder. It's not really a contact folder, but a search folder containing
    // all contacts and PDLs from all contact folders. Earlier we used "myContacts", but it had
    // several issues, so now we're using "userCuratedContacts" instead. The folder id returned by GetPeopleFilters
    // for the folder with SortGroupPriority 1 corresponds to the "myContacts" search folder, so it cannot
    // be used anymore now that we're using "userCuratedContacts" folder. The folder id for "userCuratedContacts"
    // therefore needs to be hard coded and not retrieved from GetPeopleFilters.
    const rootContactFolder = {
        displayName: rootContactFolderDistinguishedName,
        id: rootContactFolderDistinguishedName,
        children: [],
        isReadOnly: true,
        group: FolderGroupType.myContactsFolder,
    } as ContactFolder;

    const displayedContactFolders: ContactFolder[] = [];

    // Get the default contacts filter, and create folder. If there are more than one default contact filter, pick one
    // and create normal folders for the rest
    const defaultContactsFolder = getDefaultContactFolder(
        filtersToDisplay,
        displayedContactFolders
    );

    // Get the Network Folders (Linkedin, etc..) and create all folders
    const networkFolders = findPrioritiesAndCreateMultipleContactFolders(
        filtersToDisplay,
        FolderGroupType.networkFolder
    );
    displayedContactFolders.push(...networkFolders);

    //Get the Quick Contacts folder, and create folder
    const quickContactsFolder = findPriorityAndCreateContactFolder(
        filtersToDisplay,
        FolderGroupType.quickContactsFolder
    );
    if (quickContactsFolder) {
        displayedContactFolders.push(quickContactsFolder);
    }

    //Folders created by the user
    const userCreatedFolders = findPrioritiesAndCreateMultipleContactFolders(
        filtersToDisplay,
        FolderGroupType.userCreatedFolder
    );
    displayedContactFolders.push(...userCreatedFolders);

    // Contact folders that go under the Other Contacts folder (if present) in the legacy jsMVVM People Hub
    const otherFolderEntries = findPrioritiesAndCreateMultipleContactFolders(
        filtersToDisplay,
        FolderGroupType.otherFolder
    );
    displayedContactFolders.push(...otherFolderEntries);

    const publicFolders = findPublicFolders(filtersToDisplay);

    const directoryFolders = findDirectoryFolders(filtersToDisplay);

    return {
        rootContactFolder: rootContactFolder,
        defaultContactFolder: defaultContactsFolder,
        displayedFolders: displayedContactFolders || [],
        publicFolders: publicFolders || [],
        sparseFolders: directoryFolders || [],
    };
}

export function baseFolderToUserDeletedFolder(folder: BaseFolderType): ContactFolder {
    return {
        ...folderToContactFolder(folder),
        group: FolderGroupType.userCreatedFolder,
    };
}

export function folderToContactFolder(folder: PeopleFilter): ContactFolder {
    return contactFolder(folder);
}

function isSupportedFolder(folder: PeopleFilter): boolean {
    // We do not want (at this point) to display the Skype folder in consumer. We don't have enough information to identify the Skype
    // folder from the GetPeopleFilters API, so we hide all network folders. This includes the Skype folder. For consumer,
    // there should theoretically be no other network folders, as all third party social network folders have been deprecated and removed.
    if (isConsumer() && folder.SortGroupPriority === FolderGroupType.networkFolder) {
        return false;
    }

    // In enterprise, we want to filter out the LinkedIn network folder
    if (
        !isConsumer() &&
        folder.SortGroupPriority === FolderGroupType.networkFolder &&
        folder.DisplayName === WellKnownNetworkNames.LinkedIn
    ) {
        return false;
    }

    return true;
}

function findPriorityAndCreateContactFolder(
    peopleFilters: PeopleFilter[],
    priority: FolderGroupType,
    throwOnFailure?: boolean
): ContactFolder {
    const filters = findPrioritiesAndCreateMultipleContactFolders(
        peopleFilters,
        priority,
        throwOnFailure
    );
    return filters?.[0];
}

function findPrioritiesAndCreateMultipleContactFolders(
    peopleFilters: PeopleFilter[],
    priority: FolderGroupType,
    throwOnFailure?: boolean
): ContactFolder[] {
    const filters = peopleFilters.filter(filter => filter.SortGroupPriority === priority);

    if ((!filters || filters.length === 0) && throwOnFailure) {
        throw new Error(
            `Required filter not found: the contact folder structure cannot be created. Missing priority: ${priority}`
        );
    }

    return filters.map(filter => contactFolder(filter));
}

function findPublicFolders(peopleFilters: PeopleFilter[]): ContactFolder[] {
    const filters = peopleFilters.filter(
        filter => filter.SortGroupPriority >= FolderGroupType.publicFolders
    );

    return !!filters && filters.length > 0 ? filters.map(filter => contactFolder(filter)) : [];
}

function findDirectoryFolders(peopleFilters: PeopleFilter[]): ContactFolder[] {
    const filters = peopleFilters.filter(
        filter =>
            filter.SortGroupPriority >= FolderGroupType.globalAddressList &&
            filter.SortGroupPriority < FolderGroupType.publicFolders
    );

    return !!filters && filters.length > 0 ? filters.map(filter => contactFolder(filter)) : [];
}

function contactFolder(filter: PeopleFilter): ContactFolder {
    return {
        displayName: filter.DisplayName,
        id: (filter.FolderId as FolderId).Id,
        children: [],
        isReadOnly: filter.IsReadOnly,
        group: filter.SortGroupPriority,
    };
}

/**
 * Log the distribution of the different contact folder types.
 * NOTE: It is important to ONLY log the folder types, not the folder names.
 * @param contactFolders The list of contact folders (filters) as returned by the backend
 */
function logFolderStatistics(contactFolders: PeopleFilter[]): void {
    const counts: { [source: number]: number } = {};
    const networkNames: { [name: string]: number } = {};

    contactFolders.forEach(folder => {
        counts[folder.SortGroupPriority] = (counts[folder.SortGroupPriority] || 0) + 1;

        // We want to log the names of the 3rd party network folders, to help CAR team understand
        // which folder types exist, as these are being cleaned up.
        // TODO: Work Item 252985: Remove logging of 3rd party network names (once CAR team is done with the cleanup)
        if (folder.SortGroupPriority === FolderGroupType.networkFolder) {
            networkNames[folder.DisplayName] = 1;
        }
    });

    const logger = getLogger();
    if (logger) {
        logger.log('FolderTypeCounts', counts);

        if (Object.keys(networkNames).length > 0) {
            logger.log('3rdPartyNetworkNames', networkNames);
        }
    }
}

/**
 * There has been cases where the backend doesn't return a default contacts folder, and cases where it returns multiple
 * default contact folders. The logic in People Hub expects there to be just one default contact folder. If we receive
 * none, then we throw an exception. If we receive more than one, we pick one of them, and treat the rest as normal folders.
 * @param peopleFilters The list of people filters (contact folders), as returned by the backend
 * @param displayedContactFolders The list of normal contact folders to display (not including root and default)
 */
function getDefaultContactFolder(
    peopleFilters: PeopleFilter[],
    displayedContactFolders: ContactFolder[]
): ContactFolder {
    const guidRegEx: RegExp = /_[A-Za-z\d]{8}\-[A-Za-z\d]{4}\-[A-Za-z\d]{4}\-[A-Za-z\d]{4}\-[A-Za-z\d]{12}$/;

    // All default folders
    const folders = findPrioritiesAndCreateMultipleContactFolders(
        peopleFilters,
        FolderGroupType.defaultContactsFolder,
        false /* throwOnFailure */
    );

    if (!folders) {
        return null;
    }

    // If there is only one, we have to take it
    if (folders.length === 1) {
        return folders[0];
    }

    let defaultContactFolder: ContactFolder = null;

    // Look for a default folder without the GUID postfix. Push the other folders to the displayedContactFolders list
    folders.forEach(folder => {
        if (guidRegEx.test(folder.displayName)) {
            // Not interested in folders with GUID postfix
            displayedContactFolders.push(folder);
        } else if (defaultContactFolder) {
            // Already found a default folder
            displayedContactFolders.push(folder);
        } else {
            defaultContactFolder = folder;
        }
    });

    // If no default folder found, pick the first one with GUID postfix
    if (!defaultContactFolder) {
        defaultContactFolder = displayedContactFolders.shift();
    }

    // Change the folder type of the ignored "default" folders to user created folder
    displayedContactFolders.forEach(folder => (folder.group = FolderGroupType.userCreatedFolder));

    return defaultContactFolder;
}
